const HERO_IMAGE =
  'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&w=1600&q=80';

const REFERENCE_LINKS = [
  {
    label: 'USA Triathlon — multisport 101',
    url: 'https://www.usatriathlon.org/resources/multisport-101',
  },
  {
    label: 'TrainingPeaks — strength training for triathletes',
    url: 'https://www.trainingpeaks.com/blog/strength-training-for-triathletes/',
  },
  {
    label: 'Ironman — sprint triathlon training tips',
    url: 'https://www.ironman.com/news/sprint-triathlon-training-tips',
  },
];

const GYM_SESSION_A = {
  type: 'panel',
  title: 'Gym Session A — Lower body & core (Thursday)',
  intro: '45–50 minutes. Supports bike power and run durability. Rest 60–90 sec between strength sets unless noted.',
  list: [
    'Warm-up: 5 min bike or row + dynamic leg swings and hip circles.',
    'Goblet squat or back squat: 3 × 8–10 at RPE 7 (leave 2 reps in the tank).',
    'Romanian deadlift (barbell or dumbbell): 3 × 8–10—hamstrings and glutes for the run off the bike.',
    'Walking lunges: 2 × 12 steps each leg—single-leg stability for uneven roads and trails.',
    'Calf raises (standing, slow eccentric): 3 × 15—ankle stiffness for running economy.',
    'Plank circuit: 3 rounds—45 sec front plank, 30 sec side plank each side, 12 dead bugs.',
    'Glute bridge or hip thrust: 3 × 12—drives the bike pedal stroke and uphill running.',
    'Cool-down: 5 min easy walk + quad and hip flexor stretches.',
  ],
};

const GYM_SESSION_B = {
  type: 'panel',
  title: 'Gym Session B — Upper body & swim power (Tuesday)',
  intro: '40–45 minutes. Builds pull strength and shoulder stability for freestyle. Rest 60 sec between sets.',
  list: [
    'Warm-up: band pull-aparts × 15, shoulder circles, 5 min easy cardio.',
    'Lat pulldown or assisted pull-up: 3 × 8–10—mimics the catch and pull phase of freestyle.',
    'Dumbbell row (single-arm): 3 × 10 each arm—rotation and back strength for swimming.',
    'Push-up or dumbbell bench press: 3 × 10—balanced shoulder development.',
    'Face pulls or band external rotations: 3 × 15—protect rotator cuffs from overuse.',
    'Pallof press (anti-rotation): 3 × 10 each side—core stability for all three disciplines.',
    'Box step-ups or low box jumps: 2 × 8 each leg—plyometric power for the run (skip if knees are sore).',
    'Cool-down: band stretches for lats and chest; 5 min easy walk.',
  ],
};

const RECOVERY_PANEL = {
  type: 'panel',
  title: 'Recovery rules for this week',
  list: [
    'Sleep 7–9 hours—strength and endurance adaptations happen during rest, not during the workout.',
    'Eat protein within 60–90 minutes after gym and brick sessions (20–30 g is a practical target).',
    'Foam roll quads, calves, and upper back after bricks and long bike days.',
    'If legs feel heavy on Saturday, cut the brick run to 15 min easy instead of skipping the bike.',
    'Never stack Gym Session A on the same day as a hard brick—this plan keeps them separated.',
    'Stop for chest pain, dizziness, or sharp joint pain and seek medical care.',
  ],
};

/** One-week gym + swim, bike, run plan for sprint triathlon (750 m · 13 mi · 5K) */
export const TRIATHLON_SPRINT_WEEK_GYM_POST = {
  id: 'featured-triathlon-sprint-week-gym',
  slug: 'sprint-triathlon-week-gym-swim-bike-run-plan',
  title: 'Sprint Triathlon Week: Gym + Swim, Bike & Run (7-Day Plan)',
  excerpt:
    'A complete training week for sprint triathlon—750 m swim, 13-mile bike, 5K run—with two gym sessions for strength, a brick workout, and daily discipline targets. Not medical advice.',
  publishedAt: '2026-07-26T12:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Endurance Desk',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'Build your full sprint plan',
  relatedPosts: [
    {
      slug: 'triathlon-sprint-training-plan-one-month-before-race',
      title: 'Sprint Triathlon Plan: 4 Weeks Before Race Day',
      blurb: 'repeat this week structure across a full month with progressive volume.',
    },
    {
      slug: 'swim-mile-practice-tips-technique-videos',
      title: 'How to Train for a Mile Swim',
      blurb: 'extend your swim endurance beyond the 750 m race distance.',
    },
    {
      slug: 'triathlon-running-shoes-wetsuits-nutrition-guide',
      title: 'Triathlon Gear & Nutrition Guide',
      blurb: 'fuel and equip yourself for gym days and brick workouts.',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Week overview',
      items: [
        { value: '7 days', label: 'Full training week' },
        { value: 'Sprint', label: '750 m · 13 mi · 5K' },
        { value: '2×', label: 'Gym sessions' },
        { value: '6–7 hrs', label: 'Total volume' },
      ],
    },
    {
      type: 'panel',
      title: 'Who this week is for',
      paragraphs: [
        'This is a sample training week for sprint-distance triathlon: 750 m swim, 13-mile bike, and 5K run. It pairs two gym sessions with swim, bike, run, and one brick workout—the combination that builds real multisport fitness.',
        'You should already swim 400 m non-stop, bike 45 minutes, and run 20 minutes before attempting this week. Use it as a building block inside a larger plan, or repeat it 2–3 times with small volume increases each cycle.',
        'Get medical clearance before starting if you have heart disease, uncontrolled blood pressure, diabetes, joint injuries, or have been mostly inactive. Gym work uses moderate loads—never chase max lifts during triathlon training blocks.',
      ],
      chips: ['Sprint distance', 'Gym + multisport', 'Brick workout', 'Strength for triathlon'],
    },
    {
      type: 'panel',
      title: 'How this week is organized',
      orderedList: [
        'Monday: Easy recovery—light swim or rest to absorb the prior weekend.',
        'Tuesday: Swim endurance + Gym Session B (upper body and swim power).',
        'Wednesday: Steady bike ride—build aerobic base and practice hydration.',
        'Thursday: Run intervals + Gym Session A (lower body and core).',
        'Friday: Swim technique and short speed reps—sharpen form before the weekend.',
        'Saturday: Brick workout—bike then run off the bike, the key sprint triathlon session.',
        'Sunday: Easy spin or walk—flush legs and prep for the next week.',
      ],
      callout:
        'RPE guide: Easy = conversational pace, full sentences. Moderate = breathing harder but controlled (RPE 5–6). Hard = short intervals only—race-pace reps last 30 sec to 5 min, never all-out except brief strides.',
    },
    {
      type: 'panel',
      title: 'Session keys — swim, bike, run',
      list: [
        'Swim easy: continuous laps at RPE 3–4; long exhale underwater, relaxed catch, two-beat kick.',
        'Swim technique: 4–6 × 50 m drill (catch-up, fingertip drag, 6-1-6) + easy swimming between.',
        'Swim speed: 8–10 × 50 m at moderate effort (RPE 6) with 45 sec rest—build turnover without trashing shoulders.',
        'Bike easy: steady cadence 85–95 rpm; practice shifting, drinking, and staying seated on climbs.',
        'Bike steady: include 3–4 × 5 min at race effort (RPE 6) with 3 min easy between.',
        'Run easy: conversational jog; walk breaks are fine on recovery days.',
        'Run intervals: short repeats at 5K effort—build speed without long ground-pounding sessions.',
        'Brick: bike then run within 10 min—first 3–5 min of the run should feel very easy; legs will feel heavy; that is normal.',
      ],
    },
    {
      type: 'table',
      title: '7-day schedule — gym, swim, bike & run',
      intro:
        'Swap days if your pool or group ride schedule differs, but keep the brick on Saturday and avoid gym on the same day as the brick. Total weekly volume: about 6–7 hours.',
      headers: ['Day', 'Session', 'Duration', 'Details'],
      rows: [
        [
          'Monday',
          'Rest or easy swim',
          '20–30 min',
          'Optional recovery swim: 300–400 m smooth freestyle, focus on long strokes. Or full rest + 10 min mobility.',
        ],
        [
          'Tuesday',
          'Swim + Gym B',
          '75 min total',
          'Swim 35 min: 200 m warm-up · 4 × 50 m drill/rest · 4 × 100 m steady (30 sec rest) · 100 m cool-down. Then Gym Session B (upper body).',
        ],
        [
          'Wednesday',
          'Bike steady',
          '50–55 min',
          '~10–11 mi flat; 3 × 5 min at race effort (RPE 6), 3 min easy between. Drink every 15–20 min.',
        ],
        [
          'Thursday',
          'Run + Gym A',
          '70 min total',
          'Run 25 min: 8 min easy · 6 × 2 min moderate (1 min jog between) · 5 min cool-down. Then Gym Session A (lower body + core).',
        ],
        [
          'Friday',
          'Swim — technique & speed',
          '35 min',
          '300 m warm-up · 8 × 50 m moderate (45 sec rest) · 4 × 25 m drill · 200 m easy cool-down.',
        ],
        [
          'Saturday',
          'Brick (key session)',
          '75–80 min total',
          'Bike 50 min (~12–13 mi, middle 12 min at race pace) → run 20 min (first 5 min very easy, last 5 min steady). Practice T2: shoes, hat, belt.',
        ],
        [
          'Sunday',
          'Easy spin or walk',
          '25–30 min',
          'Very light bike spin or brisk walk. Foam roll. Plan meals for the week ahead.',
        ],
      ],
    },
    GYM_SESSION_B,
    GYM_SESSION_A,
    {
      type: 'panel',
      title: 'Why gym work matters for sprint triathlon',
      paragraphs: [
        'Sprint triathlon is not just about logging miles. Two short gym sessions per week improve running economy, bike power, and swim pull strength without adding heavy fatigue—if you keep loads moderate and reps controlled.',
        'Lower-body strength (squats, lunges, RDLs) protects knees and hips during the 5K run, especially the first mile off the bike when form breaks down. Core work (planks, dead bugs, Pallof press) keeps your body stable on the bike and prevents lower-back ache during long rides.',
        'Upper-body and shoulder work supports freestyle: stronger lats and stable rotator cuffs let you hold form when fatigue hits at 600 m in the swim. Skip max-effort lifting during race-prep weeks—moderate triathlon-specific strength is the goal.',
      ],
      chips: ['Injury prevention', 'Run off the bike', 'Swim pull power', 'Bike seated power'],
    },
    {
      type: 'panel',
      title: 'Brick workout tips (Saturday)',
      orderedList: [
        'Set up transition before you start: run shoes, socks, hat, race belt with bib, water for the run.',
        'Bike the first 10 min easy—heart rate spikes if you hammer out of the driveway.',
        'Middle 12 min at race effort: this is your dress rehearsal for race pace on tired legs.',
        'In T2, walk 30 sec if needed—nobody wins the race in transition, but smooth transitions save minutes.',
        'Run first 5 min very easy; walk 1 min if calves or quads scream—that is the bike-to-run adaptation.',
        'Last 5 min steady: simulate the final kilometer of the 5K when you still need to push.',
        'Note how you felt at minute 3 of the run—that sensation returns on race day.',
      ],
    },
    {
      type: 'panel',
      title: 'Scaling this week',
      list: [
        'Beginner / first sprint: cut Saturday brick to bike 40 min + run 15 min easy; skip box jumps in Gym B.',
        'Intermediate: add 200 m to Tuesday swim and 2 mi to Wednesday bike; keep gym loads the same.',
        'Advanced: repeat Saturday brick with 15 min race-pace bike + 25 min run; add a 4th swim on Monday (600 m steady).',
        'Repeat this week 2–3 times, adding 5–10% volume per cycle, then follow the 4-week sprint plan for taper.',
      ],
    },
    RECOVERY_PANEL,
  ],
  referenceLinks: REFERENCE_LINKS,
};
